import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Loader2,
  CreditCard,
  Check,
  X,
  Eye,
  EyeOff,
  ExternalLink,
  AlertTriangle,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const PaymentConfigPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    mollie: { is_enabled: false, is_test_mode: true, has_api_key: false },
    stripe: { is_enabled: false, is_test_mode: true, has_api_key: false }
  });

  // Form state
  const [mollieKey, setMollieKey] = useState('');
  const [mollieWebhook, setMollieWebhook] = useState('');
  const [stripeKey, setStripeKey] = useState('');
  const [stripeWebhook, setStripeWebhook] = useState('');
  const [showMollieKey, setShowMollieKey] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/payment-config');
      setConfig(response.data);
    } catch (error) {
      toast.error('Kon configuratie niet laden');
    } finally {
      setLoading(false);
    }
  };

  const saveMollieConfig = async () => {
    setSaving(true);
    try {
      await api.put('/admin/payment-config/mollie', {
        provider: 'mollie',
        api_key: mollieKey || undefined,
        webhook_secret: mollieWebhook || undefined,
        is_enabled: config.mollie.is_enabled,
        is_test_mode: config.mollie.is_test_mode
      });
      toast.success('Mollie configuratie opgeslagen');
      setMollieKey('');
      setMollieWebhook('');
      fetchConfig();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const saveStripeConfig = async () => {
    setSaving(true);
    try {
      await api.put('/admin/payment-config/stripe', {
        provider: 'stripe',
        api_key: stripeKey || undefined,
        webhook_secret: stripeWebhook || undefined,
        is_enabled: config.stripe.is_enabled,
        is_test_mode: config.stripe.is_test_mode
      });
      toast.success('Stripe configuratie opgeslagen');
      setStripeKey('');
      setStripeWebhook('');
      fetchConfig();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
            Configuratie
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-black dark:text-white" data-testid="payment-config-title">
            Betalingsproviders
          </h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Configureer Mollie en Stripe voor online betalingen
          </p>
        </div>

        {/* Warning */}
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Veiligheidsnotitie
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  API keys worden versleuteld opgeslagen. Gebruik test keys tijdens ontwikkeling en live keys alleen in productie.
                  API keys zijn nooit zichtbaar na opslaan.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Mollie Configuration */}
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-[#FF6B00]" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Mollie</CardTitle>
                    <CardDescription>iDEAL, creditcard, Bancontact</CardDescription>
                  </div>
                </div>
                {config.mollie.has_api_key ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Check className="h-3 w-3 mr-1" />
                    Geconfigureerd
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <X className="h-3 w-3 mr-1" />
                    Niet geconfigureerd
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Enable Switch */}
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <div>
                  <p className="font-medium text-black dark:text-white">Inschakelen</p>
                  <p className="text-xs text-neutral-500">Mollie betalingen actief maken</p>
                </div>
                <Switch
                  checked={config.mollie.is_enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    mollie: { ...prev.mollie, is_enabled: checked }
                  }))}
                  disabled={!config.mollie.has_api_key}
                />
              </div>

              {/* Test Mode Switch */}
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <div>
                  <p className="font-medium text-black dark:text-white">Test Modus</p>
                  <p className="text-xs text-neutral-500">Gebruik test API key</p>
                </div>
                <Switch
                  checked={config.mollie.is_test_mode}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    mollie: { ...prev.mollie, is_test_mode: checked }
                  }))}
                />
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label>API Key {config.mollie.has_api_key && '(al geconfigureerd)'}</Label>
                <div className="relative">
                  <Input
                    type={showMollieKey ? 'text' : 'password'}
                    value={mollieKey}
                    onChange={(e) => setMollieKey(e.target.value)}
                    placeholder={config.mollie.has_api_key ? '••••••••••••••••' : 'test_xxxx of live_xxxx'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowMollieKey(!showMollieKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showMollieKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Webhook Secret */}
              <div className="space-y-2">
                <Label>Webhook Secret (optioneel)</Label>
                <Input
                  type="password"
                  value={mollieWebhook}
                  onChange={(e) => setMollieWebhook(e.target.value)}
                  placeholder="Voor webhook verificatie"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <a
                  href="https://my.mollie.com/dashboard/developers/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Mollie Dashboard
                </a>
                <Button onClick={saveMollieConfig} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Opslaan
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stripe Configuration */}
          <Card className="border-neutral-200 dark:border-neutral-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-[#635BFF]" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Stripe</CardTitle>
                    <CardDescription>Creditcard, Apple Pay, Google Pay</CardDescription>
                  </div>
                </div>
                {config.stripe.has_api_key ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Check className="h-3 w-3 mr-1" />
                    Geconfigureerd
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <X className="h-3 w-3 mr-1" />
                    Niet geconfigureerd
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Enable Switch */}
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <div>
                  <p className="font-medium text-black dark:text-white">Inschakelen</p>
                  <p className="text-xs text-neutral-500">Stripe betalingen actief maken</p>
                </div>
                <Switch
                  checked={config.stripe.is_enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    stripe: { ...prev.stripe, is_enabled: checked }
                  }))}
                  disabled={!config.stripe.has_api_key}
                />
              </div>

              {/* Test Mode Switch */}
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <div>
                  <p className="font-medium text-black dark:text-white">Test Modus</p>
                  <p className="text-xs text-neutral-500">Gebruik test API key</p>
                </div>
                <Switch
                  checked={config.stripe.is_test_mode}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    stripe: { ...prev.stripe, is_test_mode: checked }
                  }))}
                />
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label>Secret Key {config.stripe.has_api_key && '(al geconfigureerd)'}</Label>
                <div className="relative">
                  <Input
                    type={showStripeKey ? 'text' : 'password'}
                    value={stripeKey}
                    onChange={(e) => setStripeKey(e.target.value)}
                    placeholder={config.stripe.has_api_key ? '••••••••••••••••' : 'sk_test_xxxx of sk_live_xxxx'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeKey(!showStripeKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showStripeKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Webhook Secret */}
              <div className="space-y-2">
                <Label>Webhook Secret (optioneel)</Label>
                <Input
                  type="password"
                  value={stripeWebhook}
                  onChange={(e) => setStripeWebhook(e.target.value)}
                  placeholder="whsec_xxxx"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <a
                  href="https://dashboard.stripe.com/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Stripe Dashboard
                </a>
                <Button onClick={saveStripeConfig} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Opslaan
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-black dark:text-white mb-2">
                  Webhook URLs
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">
                  Configureer deze webhook URLs in uw betaalprovider dashboard:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Mollie</Badge>
                    <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
                      {window.location.origin}/api/webhooks/mollie
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Stripe</Badge>
                    <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
                      {window.location.origin}/api/webhooks/stripe
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PaymentConfigPage;
